import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, ArrowLeft, Lock, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '../lib/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('Invalid reset link. Please request a new one.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword({ token, new_password: password });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Left Panel - Branding */}
      <div className="auth-left hidden md:flex relative bg-slate-900 w-1/2 items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20 pointer-events-none"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1760239037245-a372db8630f3?crop=entropy&cs=srgb&fm=jpg&q=85)'
          }}
        />
        <div className="relative z-10 text-center px-12">
          <div className="w-20 h-20 bg-blue-600 rounded-sm mx-auto mb-8 flex items-center justify-center">
            <Shield size={40} className="text-white" />
          </div>
          <h2 className="font-secondary font-bold text-4xl text-white mb-4">
            Zentry
          </h2>
          <p className="text-slate-400 text-lg max-w-md">
            Enterprise-grade Governance, Risk, and Compliance platform.
            Map once, comply with many frameworks.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8 text-center">
            <div className="w-16 h-16 bg-slate-900 rounded-sm mx-auto mb-4 flex items-center justify-center">
              <Shield size={32} className="text-white" />
            </div>
          </div>

          {!token ? (
            <div className="text-center" data-testid="reset-no-token">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle size={32} className="text-amber-600" />
              </div>
              <h1 className="font-secondary font-bold text-3xl text-slate-900 mb-2">
                Invalid Reset Link
              </h1>
              <p className="text-slate-500 mb-8">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
              <Link to="/forgot-password">
                <Button className="w-full h-11 bg-slate-900 hover:bg-slate-800 rounded-sm font-medium">
                  Request New Reset Link
                </Button>
              </Link>
            </div>
          ) : success ? (
            <div className="text-center" data-testid="reset-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h1 className="font-secondary font-bold text-3xl text-slate-900 mb-2">
                Password Reset!
              </h1>
              <p className="text-slate-500 mb-8">
                Your password has been updated successfully. You can now sign in with your new password.
              </p>
              <Link to="/login">
                <Button
                  data-testid="go-to-login-btn"
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800 rounded-sm font-medium"
                >
                  Sign In
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-secondary font-bold text-3xl text-slate-900 mb-2">
                Set new password
              </h1>
              <p className="text-slate-500 mb-8">
                Choose a strong password with at least 6 characters.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="password" className="text-slate-700">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    data-testid="reset-password-input"
                    placeholder="Min. 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-1.5 h-11 rounded-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    data-testid="reset-confirm-password-input"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="mt-1.5 h-11 rounded-sm"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="reset-submit-btn"
                  disabled={loading}
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800 rounded-sm font-medium"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Resetting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Lock size={18} />
                      Reset Password
                    </span>
                  )}
                </Button>
              </form>

              <p className="mt-6 text-center text-slate-500">
                <Link
                  to="/login"
                  data-testid="back-to-login-link"
                  className="text-blue-600 font-medium hover:underline inline-flex items-center gap-1"
                >
                  <ArrowLeft size={14} />
                  Back to Sign In
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
